/**
 * Owner-first block catalogue — the client side of /api/block-catalogue.
 * See BLOCKING_REDESIGN_PLAN.md §3 (register) and §6 (modal spec).
 *
 * The endpoint only exists when the backend runs with BLOCKS_V2 armed. When it
 * is absent (production until Phase 2 ships) `available` stays false and every
 * caller falls back to the legacy stage/sub-team modal — no flag duplicated on
 * the frontend, the backend's own answer decides.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _promise = null;
let _cache = { catalogue: null, available: false, ready: false };

export function loadBlockCatalogue() {
  if (!_promise) {
    _promise = axios
      .get(`${API}/block-catalogue`)
      .then((r) => { _cache = { catalogue: r.data, available: true, ready: true }; return _cache; })
      .catch(() => { _cache = { catalogue: null, available: false, ready: true }; return _cache; });
  }
  return _promise;
}

export function useBlockCatalogue() {
  const [state, setState] = useState(_cache);
  useEffect(() => {
    let alive = true;
    loadBlockCatalogue().then((s) => { if (alive) setState(s); });
    return () => { alive = false; };
  }, []);
  return state;
}

/** Owners offered at this stage, in registry order, external first. */
export function ownersForStage(cat, stage) {
  const keys = cat?.by_stage?.[stage]?.owners || [];
  return keys.map((k) => ({ key: k, ...(cat.owners[k] || {}) }));
}

/** Codes for one owner at one stage — what step 2 of the modal lists. */
export function codesForStage(cat, stage, owner) {
  if (!cat) return [];
  const allowed = new Set(cat.by_stage?.[stage]?.codes || []);
  return cat.codes.filter((c) => c.owner === owner && allowed.has(c.code));
}

export function codeMeta(cat, code) {
  if (!cat || !code) return null;
  return cat.codes.find((c) => c.code === code) || null;
}

export function ownerMeta(cat, owner) {
  return (cat?.owners || {})[owner] || null;
}

/** Blocks still open on an item, oldest first — the mirror's own ordering. */
export function openBlocks(item) {
  return (item?.blocks || [])
    .filter((b) => b && !b.cleared_at)
    .sort((a, b) => String(a.raised_at || "").localeCompare(String(b.raised_at || "")));
}

/** Whole days a block has been open. */
export function blockAgeDays(block) {
  const t = Date.parse(block?.raised_at || "");
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

/** "none" | "due" | "overdue" — an overdue promise is the board's red flag. */
export function promiseState(block) {
  const d = (block?.expected_unblock_at || "").trim();
  if (!d) return "none";
  const end = Date.parse(`${d}T23:59:59`);
  if (Number.isNaN(end)) return "none";
  return Date.now() > end ? "overdue" : "due";
}

/** One short human line for a block — used by chips, tooltips and popovers. */
export function blockHeadline(cat, block) {
  const meta = codeMeta(cat, block?.code);
  if (meta) return meta.reason;
  return (block?.note || "").trim() || "Blocked";
}
